import type { GameState } from '@susdle/shared';
import type { LobbyView, LobbyPlayer } from '../lobby/types.js';

const DEFAULT_AVATAR_PATTERN = /^https:\/\/cdn\.discordapp\.com\/embed\/avatars\/([0-5])\.png$/;

/**
 * Detects Discord's flat default-avatar URLs and extracts the 0-5 index.
 * Custom avatars (cdn.discordapp.com/avatars/<userId>/<hash>.png?size=128) and
 * any other URL → { isDefaultAvatar: false }. Discord's default-avatar regime is
 * pinned in `discordAvatar.ts:1-12` — keep this regex in sync if Discord changes
 * the path.
 */
export function classifyAvatarUrl(url: string): { isDefaultAvatar: boolean; defaultAvatarIndex?: 0|1|2|3|4|5 } {
  const match = DEFAULT_AVATAR_PATTERN.exec(url);
  if (match === null) return { isDefaultAvatar: false };
  const idx = Number(match[1]) as 0|1|2|3|4|5;
  return { isDefaultAvatar: true, defaultAvatarIndex: idx };
}

export interface ViewMappingContext {
  meId: string;
  systemMessage?: string;
}

export function stateToView(state: GameState, ctx: ViewMappingContext): LobbyView {
  // MapSchema preserves insertion order, which IS the server-side join order —
  // no explicit sort needed. Player schema has no `joinedAt` field as of
  // 2026-05-14; if one is added later, sorting can be reintroduced.
  const players: LobbyPlayer[] = [];
  for (const [, player] of state.players) {
    const avatarInfo = classifyAvatarUrl(player.avatarUrl);
    const lobbyPlayer: LobbyPlayer = {
      id: player.id,
      name: player.name,
      avatarUrl: player.avatarUrl,
      isDefaultAvatar: avatarInfo.isDefaultAvatar,
    };
    if (avatarInfo.defaultAvatarIndex !== undefined) {
      lobbyPlayer.defaultAvatarIndex = avatarInfo.defaultAvatarIndex;
    }
    players.push(lobbyPlayer);
  }
  const view: LobbyView = {
    players,
    hostId: state.hostId,
    meId: ctx.meId,
    settings: {
      foolEnabled: state.settings.foolEnabled,
      timerPreset: state.settings.timerPreset,
    },
  };
  if (ctx.systemMessage !== undefined) view.systemMessage = ctx.systemMessage;
  return view;
}
